// CheckoutPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './checkout.scss';
import FurnitureNavbar from '../../../../components/layout/Nav';
import withAuth from '../../../auth/WithAuth';
import { serverUrlAuth } from '../../../../serverUrl';
import { toast } from 'react-toastify';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state || {};
  const selectedItemIds = locationState.selectedItems || [];

  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedCartItems, setSelectedCartItems] = useState([]);
  const [userProfile, setUserProfile] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    shipping_address: '',
    phone_number: '',
  });

  // Form errors
  const [errors, setErrors] = useState({});

  // Redirect if no items selected
  useEffect(() => {
    if (!selectedItemIds || selectedItemIds.length === 0) {
      toast.warning('Please select items to checkout');
      navigate('/cart');
    }
  }, [selectedItemIds, navigate]);

  // Fetch cart and user profile on mount
  useEffect(() => {
    if (selectedItemIds?.length) {
      fetchCart();
      fetchUserProfile();
    }
  }, []);

  // Handle Paystack callback when returning from payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference');
    
    if (reference) {
      const verifyPayment = async () => {
        try {
          setProcessingPayment(true);
          // ✅ Use full URL path with /api/checkout/
          const response = await serverUrlAuth.get(`checkout/verify/?reference=${reference}`);
          
          console.log('Payment verification response:', response.data);
          
          if (response.data.status === 'success') {
            toast.success('Payment successful! Your order has been confirmed.');
            
            navigate('/order-confirmation', {
              state: {
                orderId: response.data.order_id,
                receipt_number: response.data.receipt_number,
                items: selectedCartItems,
                subtotal: subtotal,
                vat: vat,
                total: total,
                payment_method: 'Paystack',
              },
            });
          } else {
            toast.error('Payment verification failed. Please contact support.');
            navigate('/cart');
          }
        } catch (error) {
          console.error('Verification error:', error);
          toast.error('Payment verification failed. Please contact support.');
          navigate('/cart');
        } finally {
          setProcessingPayment(false);
        }
      };

      verifyPayment();
    }
  }, [location]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      // ✅ Use full API path
      const response = await serverUrlAuth.get('cart/');
      console.log('Cart response:', response.data);

      const cartData = response.data;
      const cartItemsArray = cartData.items || [];

      // Filter only selected items
      const transformedItems = cartItemsArray
        .filter((item) => selectedItemIds.includes(item.id))
        .map((item) => ({
          id: item.id,
          productId: item.product?.id,
          name: item.product?.name || 'Product',
          price: parseFloat(item.product?.price || 0),
          quantity: item.quantity,
          image:
            item.product?.images?.[0]?.image ||
            item.product?.images?.[0] ||
            '🛒',
          inStock: (item.product?.stock || 0) > 0,
          stockCount: item.product?.stock || 0,
        }));

      console.log('Selected items for checkout:', transformedItems);
      setSelectedCartItems(transformedItems);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Failed to load selected items');
      navigate('/cart');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      // ✅ Use full API path
      const response = await serverUrlAuth.get('users/address-details/');
      console.log('User profile:', response.data);
      setUserProfile(response.data);

      // Pre-fill form with last used data if available
      if (response.data.last_shipping_address) {
        setFormData((prev) => ({
          ...prev,
          shipping_address: response.data.last_shipping_address || '',
          phone_number: response.data.last_phone_number || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  // Calculate totals
  const subtotal = selectedCartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const vat = subtotal * 0.16; // 16% VAT
  const total = subtotal + vat;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.shipping_address.trim()) {
      newErrors.shipping_address = 'Shipping address is required';
    }
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required';
    } else if (
      !/^(\+?\d{1,3}[-.]?)?\d{10}$/.test(
        formData.phone_number.replace(/\s/g, ''),
      )
    ) {
      newErrors.phone_number = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCheckout = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (selectedCartItems.length === 0) {
      toast.error('No items selected for checkout');
      return;
    }

    setProcessingPayment(true);

    try {
      const cartItemIds = selectedCartItems.map(item => item.id);
      
      // ✅ Use full API path for checkout
      const response = await serverUrlAuth.post('checkout/', {
        cart_items: cartItemIds,
        shipping_address: formData.shipping_address,
        phone_number: formData.phone_number,
      });

      console.log('Checkout response:', response.data);

      if (response.status === 201 && response.data.payment?.authorization_url) {
        toast.info('Redirecting to payment gateway...');
        // ✅ Redirect to Paystack
        window.location.href = response.data.payment.authorization_url;
      } else {
        toast.error('Checkout failed: ' + (response.data.error || 'Unknown error'));
        setProcessingPayment(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to process checkout. Please try again.';
      toast.error(errorMessage);
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <>
        <FurnitureNavbar />
        <div className="checkout-page">
          <div className="checkout-loading">
            <div className="spinner"></div>
            <p>Loading checkout...</p>
          </div>
        </div>
      </>
    );
  }

  if (selectedCartItems.length === 0) {
    return (
      <>
        <FurnitureNavbar />
        <div className="checkout-page">
          <div className="empty-checkout">
            <div className="empty-icon">📦</div>
            <h2>No items selected</h2>
            <p>
              Please select items from your cart before proceeding to checkout
            </p>
            <Link to="/cart" className="back-to-cart-btn">
              <svg viewBox="0 0 24 24">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
              Back to Cart
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <FurnitureNavbar />
      <div className="checkout-page">
        <div className="checkout-container">
          {/* Header */}
          <div className="checkout-header">
            <Link to="/cart" className="back-link">
              <svg viewBox="0 0 24 24">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
              Back to Cart
            </Link>
            <h1>Checkout</h1>
          </div>

          <div className="checkout-content">
            {/* Left Column - Forms */}
            <div className="checkout-forms">
              {/* Shipping Information */}
              <div className="form-section">
                <h2>Shipping Information</h2>

                <div className="form-group">
                  <label>Shipping Address *</label>
                  <textarea
                    name="shipping_address"
                    value={formData.shipping_address}
                    onChange={handleInputChange}
                    className={errors.shipping_address ? 'error' : ''}
                    placeholder="Enter your full shipping address"
                    rows="4"
                  />
                  {errors.shipping_address && (
                    <span className="error-message">
                      {errors.shipping_address}
                    </span>
                  )}
                  <small className="hint">
                    Include street address, city, state, and postal code
                  </small>
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    className={errors.phone_number ? 'error' : ''}
                    placeholder="e.g., 0712 345 678"
                  />
                  {errors.phone_number && (
                    <span className="error-message">{errors.phone_number}</span>
                  )}
                  <small className="hint">
                    We'll use this to contact you about your order
                  </small>
                </div>
              </div>

           
            </div>

            {/* Right Column - Order Summary */}
            <div className="order-summary">
              <h2>Order Summary</h2>

              <div className="items-list">
                {selectedCartItems.map((item) => (
                  <div key={item.id} className="summary-item">
                    <div className="item-image">
                      {typeof item.image === 'string' &&
                      item.image.startsWith('http') ? (
                        <img src={item.image} alt={item.name} />
                      ) : (
                        <span>{item.image}</span>
                      )}
                    </div>
                    <div className="item-details">
                      <h4>{item.name}</h4>
                      <span className="item-quantity">
                        Qty: {item.quantity}
                      </span>
                    </div>
                    <div className="item-price">
                      KSh {(item.price * item.quantity).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              <div className="summary-calculations">
                <div className="summary-row">
                  <span>Subtotal ({selectedCartItems.length} items)</span>
                  <span>KSh {subtotal.toLocaleString()}</span>
                </div>
                <div className="summary-row">
                  <span>VAT (16%)</span>
                  <span>KSh {vat.toLocaleString()}</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>KSh {total.toLocaleString()}</span>
                </div>
              </div>

              <button
                className="payment-btn"
                onClick={handleCheckout}
                disabled={processingPayment}
              >
                {processingPayment ? (
                  <>
                    <span className="spinner-small"></span>
                    Processing...
                  </>
                ) : (
                  <>Proceed to Payment (KSh {total.toLocaleString()})</>
                )}
              </button>

              <p className="terms-notice">
                By proceeding, you agree to our{' '}
                <Link to="/terms">Terms of Service</Link> and{' '}
                <Link to="/privacy">Privacy Policy</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading overlay */}
      {processingPayment && (
        <div className="payment-overlay">
          <div className="payment-loading">
            <div className="spinner-large"></div>
            <h3>Processing your payment...</h3>
            <p>Please wait while we redirect you to the payment gateway</p>
          </div>
        </div>
      )}
    </>
  );
};

export default withAuth(CheckoutPage, 'client');